import { Breadcrumb, BreadcrumbItem, BreadcrumbLink } from '@apollo/orbit';
import { AnimatePresence, motion } from 'framer-motion';
import React, { memo, useState } from 'react';

import { ShimmerLoading } from 'src/components/common/loading/ShimmerLoading';

import { FieldMetrics } from '../../useCurrentField';

import { FieldPageClientMetric } from './FieldPageClientMetric';
import { FieldPageClientPanel } from './fieldPageClientPanel/FieldPageClientPanel';

interface Props {
  fieldMetrics: FieldMetrics;
  loading?: boolean;
}

const CLIENT_SECTIONS_WHILE_LOADING = 4;

const ClientShimmer = memo(() => (
  <div className="border-b border-primary py-3">
    <div className="ml-6">
      <ShimmerLoading key="1" className="my-2 w-56" />
      <ShimmerLoading key="2" className="my-2 w-80" />
    </div>
  </div>
));

const ClientBreadcrumbs = ({
  onAllClientsClick,
  selectedClientName,
}: {
  onAllClientsClick: () => void;
  selectedClientName?: string;
}) => (
  <Breadcrumb className="border-b border-b-primary px-6 py-1">
    <BreadcrumbItem>
      <BreadcrumbLink onClick={onAllClientsClick}>All clients</BreadcrumbLink>
    </BreadcrumbItem>
    {selectedClientName !== undefined && (
      <BreadcrumbItem isCurrentPage>
        <BreadcrumbLink>
          {selectedClientName || 'Unidentified clients'}
        </BreadcrumbLink>
      </BreadcrumbItem>
    )}
  </Breadcrumb>
);

const slideInVariants = {
  hidden: {
    x: '100%',
    transition: {
      bounce: 0,
    },
  },
  visible: {
    x: 0,
    transition: {
      bounce: 0,
    },
  },
  exit: {
    x: '100%',
    transition: {
      bounce: 0,
    },
  },
};

const slideOutVariants = {
  hidden: {
    x: '-100%',
    transition: {
      bounce: 0,
    },
  },
  visible: {
    x: 0,
    transition: {
      bounce: 0,
    },
  },
  exit: {
    x: '-100%',
    transition: {
      bounce: 0,
    },
  },
};

export const FieldPageClientMetrics = ({ fieldMetrics, loading }: Props) => {
  const [selectedClientName, setSelectedClientName] = useState<
    string | undefined
  >();
  const selectedClientMetrics =
    selectedClientName !== undefined &&
    fieldMetrics.clientMetrics.get(selectedClientName);
  return (
    <>
      <div className="relative h-full max-h-[480px] min-h-[300px]">
        {loading ? (
          <>
            {Array.from(Array(CLIENT_SECTIONS_WHILE_LOADING).keys()).map(
              (i) => (
                <ClientShimmer key={i} />
              ),
            )}
          </>
        ) : (
          <AnimatePresence initial={false}>
            {selectedClientMetrics ? (
              <motion.div
                key="single-client"
                className="absolute inset-0 flex flex-col overflow-hidden"
                variants={slideInVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <ClientBreadcrumbs
                  selectedClientName={selectedClientName}
                  onAllClientsClick={() => setSelectedClientName(undefined)}
                />
                <div className="hide-scrollbar flex-1 overflow-y-auto">
                  <FieldPageClientPanel clientMetrics={selectedClientMetrics} />
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="all-clients"
                variants={slideOutVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="hide-scrollbar absolute inset-0 overflow-y-auto pb-6"
              >
                {Array.from(fieldMetrics.clientMetrics.entries()).map(
                  ([clientName, clientMetrics]) => (
                    <FieldPageClientMetric
                      key={clientName}
                      clientName={clientName}
                      clientMetrics={clientMetrics}
                      onClick={setSelectedClientName}
                    />
                  ),
                )}
              </motion.div>
            )}
          </AnimatePresence>
        )}
      </div>
    </>
  );
};
